import React, { type FC, type ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import i18n from 'i18next';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { type ProviderData } from 'components/Auth';
import { useProviderCountryCode } from 'utils/regionUtils';

interface DtrumApi {
  identifyUser: (userId: string) => void;
  sendSessionProperties: (
    javaLongOrObject?: Record<string, number>,
    date?: Record<string, Date>,
    shortString?: Record<string, string>,
    javaDouble?: Record<string, number>
  ) => unknown;
  enterAction: (actionName: string, actionType?: string, startTime?: number, sourceUrl?: string) => number;
  leaveAction: (actionId: number) => void;
}

declare global {
  interface Window {
    dtrum?: DtrumApi;
  }
}

const SUPPORT_ID_STORAGE_KEY = 'telemetry-support-id';
const DTRUM_READY_MAX_ATTEMPTS = 20;
const DTRUM_READY_RETRY_DELAY_MS = 250;

// Dynatrace's JS API exposes no browser-readable session id (confirmed: no getSessionId() equivalent
// exists on `dtrum`). This client-generated id is shown to users and sent to Dynatrace as the
// `support_id` session property so support can search for the session in the Dynatrace tenant.
export const getOrCreateSupportId = (): string => {
  try {
    const existing = window.sessionStorage.getItem(SUPPORT_ID_STORAGE_KEY);
    if (existing) return existing;
    const generated = crypto.randomUUID();
    window.sessionStorage.setItem(SUPPORT_ID_STORAGE_KEY, generated);
    return generated;
  } catch {
    return '';
  }
};

const waitForDtrum = (onReady: (dtrum: DtrumApi) => void, attemptsRemaining = DTRUM_READY_MAX_ATTEMPTS) => {
  try {
    if (window.dtrum) {
      onReady(window.dtrum);
      return;
    }
    if (attemptsRemaining <= 0) return;
    setTimeout(() => waitForDtrum(onReady, attemptsRemaining - 1), DTRUM_READY_RETRY_DELAY_MS);
  } catch (error) {
    console.error('Dynatrace is not available', error);
  }
};

// Constant ownership/infrastructure values, mirrored from the Terraform resource tags. Keys must be lowercase.
export const SERVICE_METADATA_PROPERTIES: Record<string, string> = {
  stack: 'ess',
  lob: 'symphoni',
  service_offering: 'TELUS_Health_EAP',
  managed_by: 'terraform',
  technical_team: 'symphoni-team@telushealth.com',
  cost_center: 'C1963',
  service_owner: 'dan.mciver',
  service_name: 'symphoni-providerhub-web',
  service: 'webapp',
};

// Session property keys sent below (region, env, support_id, appversion, plus every key in
// SERVICE_METADATA_PROPERTIES) must be pre-defined as Session properties (source: JavaScript API) in the
// Dynatrace tenant's Application Settings, or `sendSessionProperties` silently discards them server-side.
export const initializeTelemetry = (providerData?: ProviderData, regionCode?: string, environment = 'dev', appVersion?: string) => {
  if (!providerData) return;

  waitForDtrum((dtrum) => {
    try {
      const userId = providerData.providerId ? String(providerData.providerId) : providerData.emailAddress;
      if (userId) {
        dtrum.identifyUser(userId);
      }
      const region = regionCode ?? '';
      const { stack, lob, service_offering } = SERVICE_METADATA_PROPERTIES;
      dtrum.sendSessionProperties(undefined, undefined, {
        region,
        env: environment,
        support_id: getOrCreateSupportId(),
        appversion: appVersion ?? '',
        ...SERVICE_METADATA_PROPERTIES,
        // Same values as the fields above, under an app_ prefix.
        // This is a workaround for Dynatrace's limitation that Session property keys which are deleted take 35 days to be able to be created with the same name.
        app_region: region,
        app_env: environment,
        app_stack: stack,
        app_lob: lob,
        app_service_offering: service_offering,
      });
    } catch (error) {
      console.error('Dynatrace telemetry initialization failed', error);
    }
  });
};

export const TelemetryNavigationTracker: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const regionCode = useProviderCountryCode();

  useEffect(() => {
    try {
      const dtrum = window.dtrum;
      if (!dtrum) return;
      const actionName = regionCode ? `View: ${location.pathname} (${regionCode})` : `View: ${location.pathname}`;
      const actionId = dtrum.enterAction(actionName, 'custom');
      dtrum.leaveAction(actionId);
    } catch (error) {
      console.error('Dynatrace telemetry navigation tracker failed:', error);
    }
  }, [location.pathname, regionCode]);

  return React.createElement('div', { className: 'telemetry-navigation-tracker' }, children);
};

const ErrorStackTraceDetails: FC<{ error: Error }> = ({ error }) => {
  const [expanded, setExpanded] = useState(false);

  return React.createElement(
    'div',
    { className: `telemetry-stack-trace${expanded ? ' expanded' : ''}` },
    React.createElement(
      ButtonBase,
      {
        className: 'telemetry-stack-trace__toggle',
        onClick: () => setExpanded((prev) => !prev),
        'aria-expanded': expanded,
      },
      i18n.t(expanded ? 'hide_stack_trace_label' : 'show_stack_trace_label', { ns: 'errorMessages' }),
      React.createElement(expanded ? ExpandLessIcon : ExpandMoreIcon, { fontSize: 'small' })
    ),
    React.createElement(
      Collapse,
      { in: expanded, timeout: 'auto', unmountOnExit: true },
      React.createElement('pre', { className: 'telemetry-stack-trace__content' }, error.stack || error.message)
    )
  );
};

export const snackBarMessageWithSupportId = (message: string, error?: Error) => {
  return React.createElement(
    'div',
    null,
    React.createElement('div', null, message),
    React.createElement('br', null),
    React.createElement(
      'div',
      null,
      `${i18n.t('session_id_label', { ns: 'errorMessages' })} `,
      React.createElement(
        'span',
        { className: 'telemetry-support-id' },
        getOrCreateSupportId() || i18n.t('telemetry_not_initialized', { ns: 'errorMessages' })
      )
    ),
    error ? React.createElement(ErrorStackTraceDetails, { error }) : null
  );
};
